import { useQuery } from '@tanstack/react-query';

import { ApiError } from '@mds/shared/lib/api';

import * as admin from '@/administration/administration.api';
import Card from '@/components/ui/card';
import Chip from '@/components/ui/chip';
import EmptyState from '@/components/ui/empty-state';
import { Table, Row, Cell } from '@/components/ui/table';

/**
 * Roles and permissions, read-only.
 *
 * Phase 1 exposes these as reference data — editing them is deliberately out of
 * scope — so this explains what each role can do rather than offering controls
 * that would not work.
 */
export default function RolesPage() {
  const roles = useQuery({
    queryKey: ['admin', 'roles', 'all'],
    queryFn: () => admin.listRoles({ includeInactive: true }),
  });
  const permissions = useQuery({
    queryKey: ['admin', 'permissions'],
    queryFn: () => admin.listPermissions(),
  });

  const failed = (error: unknown) =>
    error instanceof ApiError ? error.message : 'The server could not be reached.';

  return (
    <div className="flex flex-col gap-6">
      <Card title="Roles">
        {roles.isPending ? (
          <p className="m-0 text-base text-ink-muted">Loading roles…</p>
        ) : roles.isError ? (
          <EmptyState title="Could not load roles" hint={failed(roles.error)} />
        ) : (
          <ul className="m-0 flex list-none flex-col gap-4 p-0">
            {roles.data!.map((role) => (
              <li key={role.id} className="rounded-2xl border border-hairline p-5">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-lg font-semibold">{role.name}</span>
                  {role.isSystem && <Chip>System</Chip>}
                  {!role.isActive && (
                    <Chip className="border border-danger-edge bg-danger-wash text-danger-text">
                      Inactive
                    </Chip>
                  )}
                  <span className="ml-auto font-mono text-md text-ink-muted">
                    {role.userCount} {role.userCount === 1 ? 'administrator' : 'administrators'}
                  </span>
                </div>

                {role.description && (
                  <p className="m-0 mt-2 text-base text-ink-secondary">{role.description}</p>
                )}

                <div className="mt-3 flex flex-wrap gap-1.5">
                  {role.permissions.length === 0 ? (
                    <span className="text-base text-ink-muted">No permissions granted.</span>
                  ) : (
                    role.permissions.map((p) => (
                      <Chip
                        key={p.id}
                        className="rounded-2xl border border-hairline bg-surface font-sans text-md font-medium tracking-normal whitespace-normal normal-case"
                      >
                        {p.description}
                      </Chip>
                    ))
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card title="Permissions">
        {permissions.isPending ? (
          <p className="m-0 text-base text-ink-muted">Loading permissions…</p>
        ) : permissions.isError ? (
          <EmptyState title="Could not load permissions" hint={failed(permissions.error)} />
        ) : (
          <Table head={['What it allows']}>
            {permissions.data!.map((p) => (
              <Row key={p.id}>
                <Cell>{p.description}</Cell>
              </Row>
            ))}
          </Table>
        )}
      </Card>
    </div>
  );
}
